import type {
	ConvertParams,
	ConvertResult,
	ListWithdrawalsParams,
	MerchantBalance,
	ServiceFee,
	Withdrawal,
	WithdrawalsPagination,
} from "src/types";
import apiRequest from "src/utils/api-request";
import withApiPath from "src/utils/with-api-path";

const balancesProvider = {
	async listBalances(merchantId: string): Promise<MerchantBalance[]> {
		const response = await apiRequest.get(
			withApiPath(`/merchant/${merchantId}/balance`),
		);
		return response.data?.results;
	},

	async getServiceFee(
		merchantId: string,
		balanceId: string,
	): Promise<ServiceFee> {
		const response = await apiRequest.get(
			withApiPath(`/merchant/${merchantId}/withdrawal-fee`),
			{
				params: { balanceId },
			},
		);
		return response.data;
	},

	async createWithdrawal(
		merchantId: string,
		params: {
			balanceId: string;
			recipientAddress: string;
			amount: string;
			description?: string;
			deductFeeFromAmount?: boolean;
		},
	): Promise<Withdrawal> {
		const response = await apiRequest.post(
			withApiPath(`/merchant/${merchantId}/withdrawals`),
			params,
		);
		return response.data;
	},

	async listWithdrawals(
		merchantId: string,
		params?: ListWithdrawalsParams,
	): Promise<WithdrawalsPagination> {
		// Clean up undefined/empty params
		const cleanParams = params
			? Object.fromEntries(
					Object.entries(params).filter(([, v]) => v !== undefined && v !== ""),
				)
			: undefined;
		const response = await apiRequest.get(
			withApiPath(`/merchant/${merchantId}/withdrawals`),
			{ params: cleanParams },
		);
		return response.data;
	},

	async getWithdrawal(
		merchantId: string,
		withdrawalId: string,
	): Promise<Withdrawal> {
		const response = await apiRequest.get(
			withApiPath(`/merchant/${merchantId}/withdrawals/${withdrawalId}`),
		);
		return response.data;
	},

	async cancelWithdrawal(
		merchantId: string,
		withdrawalId: string,
	): Promise<Withdrawal> {
		const response = await apiRequest.post(
			withApiPath(`/merchant/${merchantId}/withdrawals/${withdrawalId}/cancel`),
		);
		return response.data;
	},

	async getCurrencyExchangeRate(
		merchantId: string,
		params: ConvertParams,
	): Promise<ConvertResult> {
		const response = await apiRequest.get(
			withApiPath(`/merchant/${merchantId}/currency-convert`),
			{ params },
		);
		return response.data;
	},
};

export default balancesProvider;
